import { Apps, ArrowDownward, ArrowUpward, Cancel, ChevronLeft } from '@mui/icons-material';
import { HelpOutline } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  Collapse,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonProps,
  Tooltip,
  Typography
} from '@mui/material';
import { TileWithIconUpload } from '@thro-types/users';

import { Field, Form, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import StyledDropzone from 'app/components/Dropzone';
import { SaveButton } from 'app/components/SaveButton';
import { StyleType, TileType } from 'app/containers';
import { DashboardCard } from 'app/containers/Dashboard/HomePage/components/DashboardCard';
import { selectTileDrawer } from 'app/containers/Dashboard/HomePage/selectors';
import { TileDrawerActions } from 'app/containers/Dashboard/HomePage/tileDrawerSlice';
import { DrawerMode } from 'app/containers/Dashboard/HomePage/types';
import { translations } from 'locales/i18n';
import { useUnloadListener } from 'utils/hooks/useUnloadListener';

import '../styles/CreateContent.scss';

interface CardDrawerContentProps {
  type: TileType;
}

export const CreateContent = ({ type }: CardDrawerContentProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const tileDrawer = useSelector(selectTileDrawer);
  const { values, setFieldValue, errors, isSubmitting, touched, isValid } =
    useFormikContext<TileWithIconUpload>();

  const [iconPath, setIconPath] = useState(['']);
  const [showMore, setShowMore] = useState(false);
  useUnloadListener();

  const { data: entitlementGroups, isLoading } = useEntitlementGroupsQuery();

  // ? This is to prevent the editing of the `isRounded` value for defaultIcons
  useEffect(() => {
    if (iconPath[0].length !== 0) {
      setFieldValue('icon.isDefault', false);
    }
  }, [iconPath, setFieldValue, values.iconToUpload]);

  const renderTitle = () => {
    if (type !== TileType.SYSTEM) {
      return t(translations.pages.home.cardDrawer.drawerTitle());
    }
    if (tileDrawer.tile && Object.hasOwn(tileDrawer.tile, 'id')) {
      return t(translations.pages.home.cardDrawer.drawerEditSystemTitle());
    } else {
      return t(translations.pages.home.cardDrawer.drawerCreateSystemTitle());
    }
  };

  const TooltipToggleButton = (props: ToggleButtonProps) => {
    const { title, ...other } = props;
    return (
      <Tooltip title={title}>
        <ToggleButton {...other} />
      </Tooltip>
    );
  };

  const toggleShowMore = () => {
    setShowMore(!showMore);
  };

  const handleIsExternalChange = (checked: boolean) => {
    const httpRegex = new RegExp('https?://');
    setFieldValue('isExternal', checked);
    if (checked) {
      const newLink = values.link.startsWith('http') ? values.link : `https://${values.link}`;
      setFieldValue('link', newLink);
    } else {
      setFieldValue('link', values.link.replace(httpRegex, ''));
    }
  };

  return (
    <Form>
      <Grid
        container
        justifyContent={'space-between'}
        padding={2}
        flexDirection={'row'}
        minHeight={'100vh'}
        maxHeight={'100vh'}
      >
        <Grid container item alignItems={'center'}>
          {type !== TileType.SYSTEM && (
            <IconButton
              onClick={() => {
                dispatch(TileDrawerActions.setCurrentMode(DrawerMode.PREDEFINED));
              }}
            >
              <ChevronLeft />
            </IconButton>
          )}
          <Typography variant="h5" fontWeight={'bold'}>
            {renderTitle()}
          </Typography>
        </Grid>
        <Divider sx={{ marginLeft: 2, marginRight: 0.6, flex: 1 }} />
        <Grid
          container
          item
          maxHeight={'73vh'}
          overflow={'auto'}
          gap={2}
          paddingY={1}
          margin={0}
          paddingRight={1}
        >
          <Grid container item gap={2}>
            {type !== TileType.CUSTOM && (
              <Grid container justifyContent={'center'}>
                <ToggleButtonGroup
                  exclusive
                  value={values.type}
                  onChange={(e, newType) => newType !== null && setFieldValue('type', newType)}
                >
                  <TooltipToggleButton
                    title={t(translations.pages.home.cardDrawer.tooltips.systemTile())}
                    value={TileType.SYSTEM}
                  >
                    {t(translations.pages.home.cardDrawer.systemTile())}
                  </TooltipToggleButton>
                  <TooltipToggleButton
                    title={t(translations.pages.home.cardDrawer.tooltips.defaultTile())}
                    value={TileType.DEFAULT}
                  >
                    {t(translations.pages.home.cardDrawer.defaultTile())}
                  </TooltipToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
            <Grid container gap={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.pages.home.cardDrawer.title())}</FormLabel>
                <Field
                  as={TextField}
                  id="title_de"
                  name="title_de"
                  error={touched.title_de && Boolean(errors.title_de)}
                  helperText={touched.title_de && errors.title_de}
                />
              </FormControl>
              {type !== TileType.CUSTOM && (
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.home.cardDrawer.titleEN())}</FormLabel>
                  <Field
                    as={TextField}
                    id="title_en"
                    name="title_en"
                    error={touched.title_en && Boolean(errors.title_en)}
                    helperText={touched.title_en && errors.title_en}
                  />
                </FormControl>
              )}
            </Grid>
            <Grid container gap={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.pages.home.cardDrawer.shortText())}</FormLabel>
                <Field
                  as={TextField}
                  id="shortText_de"
                  name="shortText_de"
                  error={touched.shortText_de && Boolean(errors.shortText_de)}
                  helperText={touched.shortText_de && errors.shortText_de}
                />
              </FormControl>
              {type !== TileType.CUSTOM && (
                <FormControl required fullWidth>
                  <FormLabel>{t(translations.pages.home.cardDrawer.shortTextEN())}</FormLabel>
                  <Field
                    as={TextField}
                    id="shortText_en"
                    name="shortText_en"
                    error={touched.shortText_en && Boolean(errors.shortText_en)}
                    helperText={touched.shortText_en && errors.shortText_en}
                    // inputProps={{ maxLength: 75 }}
                  />
                </FormControl>
              )}
            </Grid>
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.home.cardDrawer.tileIcon())}</FormLabel>
              <Grid container flexDirection={'column'} gap={1}>
                <Grid item>
                  <StyledDropzone
                    name="iconToUpload"
                    value={values.iconToUpload}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    placeholder={t(translations.pages.home.cardDrawer.placeholder.uploadImage())}
                    className="product-create__dropzone"
                    accept={{
                      'image/jpeg': ['.jpeg', '.jpg'],
                      'image/png': ['.png'],
                      'image/svg+xml': ['.svg']
                    }}
                    single
                    setPaths={setIconPath}
                  />
                </Grid>
                <Grid container alignItems={'center'} flexDirection={'row'}>
                  <div
                    style={{
                      borderBottomStyle: 'solid',
                      borderBottomColor: 'gray',
                      borderBottomWidth: 2,
                      flex: 1,
                      marginLeft: 10
                    }}
                  />
                  <Typography flex={1} textAlign={'center'}>
                    {t(translations.common.or())}
                  </Typography>
                  <div
                    style={{
                      borderBottomStyle: 'solid',
                      borderBottomColor: 'gray',
                      borderBottomWidth: 2,
                      flex: 1,
                      marginRight: 10
                    }}
                  />
                </Grid>
                <Grid item>
                  <Button
                    sx={{ color: 'white' }}
                    variant="contained"
                    startIcon={<Apps />}
                    fullWidth
                    onClick={() =>
                      dispatch(TileDrawerActions.setCurrentMode(DrawerMode.DEFAULTICONS))
                    }
                  >
                    {t(translations.pages.home.cardDrawer.defaultIcons())}
                  </Button>
                </Grid>
              </Grid>
            </FormControl>
            {!values.icon.isDefault && (
              <FormControl required style={{ flexDirection: 'row', alignItems: 'center' }}>
                <>
                  <Checkbox
                    value={values.icon?.isRounded}
                    checked={values.icon?.isRounded}
                    id="icon.isRounded"
                    onChange={(_, checked) => {
                      const currentIcon = values.icon;
                      const updatedIcon = { ...currentIcon, isRounded: checked };
                      setFieldValue('icon', updatedIcon);
                    }}
                  />
                  <Typography>{t(translations.pages.home.cardDrawer.roundImage())}</Typography>
                </>
              </FormControl>
            )}
            <FormControl required fullWidth>
              <FormLabel>{t(translations.pages.home.cardDrawer.link())}</FormLabel>
              <Field
                as={TextField}
                id="link"
                name="link"
                error={touched.link && Boolean(errors.link)}
                helperText={touched.link && errors.link}
              />
            </FormControl>
            <FormControl style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <Grid container alignItems={'center'}>
                <Checkbox
                  value={values.isExternal}
                  checked={values.isExternal}
                  id="isExternal"
                  onChange={(_, checked) => handleIsExternalChange(checked)}
                />
                <Typography>{t(translations.pages.home.cardDrawer.external())}</Typography>
              </Grid>
              <Tooltip title={t(translations.pages.home.cardDrawer.tooltips.externalLinkTip())}>
                <HelpOutline fontSize="medium" />
              </Tooltip>
            </FormControl>
          </Grid>
          <Grid container item>
            <DashboardCard
              id={'preview'}
              fullWidth
              tile={{
                ...values,
                id: 0,
                icon: {
                  id: 0,
                  path: iconPath[0].length
                    ? iconPath[0]
                    : tileDrawer.tile?.icon.path !== values.icon.path
                    ? values.icon.path
                    : `${tileDrawer.tile.icon.path}`,
                  isRounded: values.icon?.isRounded || false,
                  isDefault: false,
                  originalName: ''
                }
              }}
            />
          </Grid>
          {type === TileType.SYSTEM && (
            <>
              <Grid container item>
                <FormControl fullWidth required>
                  <FormLabel>{t(translations.pages.home.cardDrawer.visibilityGroups())}</FormLabel>
                  <Autocomplete
                    id="visibilityGroups"
                    loading={isLoading}
                    multiple
                    options={entitlementGroups?.data || []}
                    value={values.visibilityGroups?.map(value => {
                      return { id: value } as any;
                    })}
                    getOptionLabel={option =>
                      option.name
                        ? `${option.name}`
                        : `${entitlementGroups?.data.find(eg => eg.id === option.id)?.name}`
                    }
                    renderInput={params => <TextField {...params} />}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    onChange={(_, value) => {
                      const idArray = value.map(item => item.id);
                      setFieldValue('visibilityGroups', idArray);
                    }}
                    disableCloseOnSelect
                  />
                </FormControl>
              </Grid>
              <Button
                variant="text"
                onClick={toggleShowMore}
                endIcon={showMore ? <ArrowUpward /> : <ArrowDownward />}
              >
                {showMore
                  ? t(translations.pages.home.cardDrawer.lessOptions())
                  : t(translations.pages.home.cardDrawer.moreOptions())}
              </Button>
              <FormControl fullWidth>
                <Collapse in={showMore}>
                  <FormControl fullWidth>
                    <FormLabel>{t(translations.pages.home.cardDrawer.styleType())}</FormLabel>
                    <Select
                      id="styleType"
                      name="styleType"
                      value={values.styleType || ''}
                      onChange={e => setFieldValue('styleType', e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">{t(translations.common.none())}</MenuItem>
                      {Object.values(StyleType).map(styleType => (
                        <MenuItem key={styleType} value={styleType}>
                          {styleType}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Collapse>
              </FormControl>
            </>
          )}
        </Grid>
        <Divider sx={{ marginLeft: 2, marginRight: 0.6, flex: 1 }} />
        <Grid item>
          <Alert severity="info">{t(translations.pages.home.cardDrawer.notInNavInfo())}</Alert>
        </Grid>
        <Grid container item justifyContent={'flex-end'} gap={2} alignItems={'flex-end'}>
          <Grid item>
            <Button
              variant="contained"
              style={{ color: 'white', backgroundColor: 'red' }}
              startIcon={<Cancel />}
              onClick={() => dispatch(TileDrawerActions.close())}
            >
              {t(translations.common.cancel())}
            </Button>
          </Grid>
          <SaveButton isSubmitting={isSubmitting} isValid={isValid} />
        </Grid>
      </Grid>
    </Form>
  );
};
